/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Button } from '@marriott/mi-ui-library';
import { PageParamsContext } from '../../../../modules/context/PageContext';
import { AEMInternetBarNewAgeProps, InternetBarExperienceProps, InternetBarStateTypes } from '../../InternetBar.types';
import { inStayMockResponse } from '../../__mock__/InternetBar.model';
import { INTERNET_BAR_INITIAL_STATE, NEW_AGE_UX } from '../../utils/constant';
import {
  internetBarIcons,
  analyticsTrackingIdentifier,
  getInternetBarTrackingIdentifier,
} from '../../../../modules/utils/constants/constants';
import { useSession } from '@marriott/mi-headless-utils';
import { sanitize } from '../../../../modules/utils/common';
import { isMobile } from '../../internetBar.helper';

export const InternetBarMarketingExperienceTwo: FC<InternetBarExperienceProps> = (
  pageProps: InternetBarExperienceProps
) => {
  const { preProcessorResponse } = useContext(PageParamsContext);
  const termsAndConditionsRef = useRef<HTMLParagraphElement>(null);
  const { datalayer } = useSession();
  const {
    state = '',
    currentStateMapping = {
      ...(pageProps?.isAuthorMode ? inStayMockResponse : (INTERNET_BAR_INITIAL_STATE as InternetBarStateTypes)),
    },
    userState,
  } = preProcessorResponse || {};

  const { userExperience = '' } = pageProps?.internetPricingData?.data ?? {};

  const [showIcon] = useState<boolean>(
    internetBarIcons && internetBarIcons?.[state] ? internetBarIcons?.[state]?.showIcon : false
  );

  const marketingWithIncentiveOffersTermsAndConditionsText = sanitize(
    (pageProps && pageProps?.['marketingOffersTnC' as keyof AEMInternetBarNewAgeProps]?.toString()) ?? ''
  );

  const offersTermsConditionText = sanitize(
    (pageProps && pageProps?.['offersTnCDescription' as keyof AEMInternetBarNewAgeProps]?.toString()) ?? ''
  );

  const [loyaltyJoinNow, setLoyaltyJoinNow] = useState<string>(pageProps?.joinButtonLink);
  const [showMarketingOffersTermsAndConditionsText, setShowMarketingOffersTermsAndConditionsText] =
    useState<boolean>(false);
  const [joinAndConnectButtonClickTrack, setJoinAndConnectButtonClickTrack] = useState<string>('');

  useEffect(() => {
    const { Z, zoneMapping } = pageProps?.internetBarCurrentStateData ?? {};
    if (pageProps && pageProps?.internetPricingData && pageProps?.internetPricingData?.data) {
      const { enrollmentSourceCode = '' } = pageProps?.internetPricingData?.data ?? {};
      if (enrollmentSourceCode) {
        const joinNowLink = pageProps?.handleLinks(enrollmentSourceCode, zoneMapping, Z);
        setLoyaltyJoinNow(joinNowLink);
      }
    } else if (
      pageProps?.internetBarCurrentStateData &&
      pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode']
    ) {
      const joinNowLink = pageProps?.handleLinks(
        pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode'],
        zoneMapping,
        Z
      );
      setLoyaltyJoinNow(joinNowLink);
    }
  }, [pageProps?.internetPricingData?.data]);

  useEffect(() => {
    const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData ?? {};
    const propertyPricingTier = zoneMapping?.[Z ?? 'GUEST'];
    let joinNowClickTrack = '';
    let propertyType = '';
    if (
      pageProps &&
      pageProps?.internetPricingData &&
      pageProps?.internetPricingData?.data &&
      pageProps?.internetPricingData?.data?.enrollmentSourceCode
    ) {
      const { uxlGlobalDivision, enrollmentSourceCode } = pageProps?.internetPricingData?.data ?? {};
      if (datalayer) {
        propertyType = (datalayer?.['prop_brand_tier'] as string) ?? '';
        joinNowClickTrack = `${getInternetBarTrackingIdentifier(
          state,
          analyticsTrackingIdentifier.internetBar,
          propertyPricingTier
        )}|${uxlGlobalDivision
          ?.toString()
          ?.replaceAll(' ', '')};${propertyType};${userState};${enrollmentSourceCode};JoinAndConnect|internal`;
      }
    } else {
      joinNowClickTrack = `${getInternetBarTrackingIdentifier(
        state,
        analyticsTrackingIdentifier.internetBar,
        propertyPricingTier
      )}|JoinAndConnect|internal`;
    }
    setJoinAndConnectButtonClickTrack(joinNowClickTrack);
  }, [pageProps, pageProps?.internetPricingData, pageProps?.internetBarCurrentStateData, userState, datalayer]);

  useEffect(() => {
    if (
      userExperience?.toLowerCase() === NEW_AGE_UX.marketingWithIncentive &&
      termsAndConditionsRef &&
      termsAndConditionsRef?.current
    ) {
      const termsAndConditionsLinks = termsAndConditionsRef.current.getElementsByTagName('a');
      if (termsAndConditionsLinks.length) {
        const offersTermsConditionLink = termsAndConditionsLinks[0];
        const handleClick = () => {
          setShowMarketingOffersTermsAndConditionsText(true);
          setTimeout(() => {
            pageProps?.resizeImage && pageProps?.resizeImage();
            if (isMobile()) {
              offersTermsConditionLink?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 0);
        };
        offersTermsConditionLink.addEventListener('click', handleClick);

        // Cleanup function to remove the event listener on component unmount
        return () => {
          offersTermsConditionLink.removeEventListener('click', handleClick);
        };
      }
    }
    return undefined;
  }, [userExperience, termsAndConditionsRef, pageProps]);

  return currentStateMapping && pageProps ? (
    <div className={'col-12 d-flex flex-column justify-content-center px-0 text-center align-items-center'}>
      {/* 1. Internet Bar Icon Configuration */}
      {showIcon && (
        <span className={`t-accent-color d-inline-block ${internetBarIcons?.[state]?.iconName}  icon-xxl`}></span>
      )}
      {/* 2. Title */}
      <h3 className={`col-12 t-subtitle-xl my-4 px-5 px-md-3`}>
        {userExperience?.toLowerCase() === NEW_AGE_UX.marketingWithIncentive
          ? pageProps?.newAgeExp ?? ''
          : pageProps?.title ?? ''}
      </h3>

      <div className={'loyalty-container'}>
        <div className={'d-flex flex-column'}>
          <p className={'t-font-s loyalty-description-title mb-2'}>
            {userExperience?.toLowerCase() === NEW_AGE_UX.marketingWithIncentive
              ? pageProps?.joinMBVMemberLabel
              : pageProps?.loyaltyJoinnowScenarioText}
          </p>
          <p className={'t-font-s loyalty-description mb-0'}>
            {userExperience?.toLowerCase() === NEW_AGE_UX.marketingWithIncentive
              ? pageProps?.marketingBonusPointsDesc
              : pageProps?.notMBVMemberDesc}
          </p>
        </div>
        <div className={'d-flex flex-column justify-content-between align-items-center'}>
          <Button
            className={`m-button-primary mt-4 loyalty-buttons d-flex align-items-center justify-content-center ${
              !pageProps?.enableLoyaltyJoinNowBtn ? 'disabled' : ''
            }`}
            isLink={true}
            href={loyaltyJoinNow ?? pageProps?.joinButtonLink}
            trackingProperties={pageProps?.trackingProperties}
            target={pageProps?.joinButtonLinkInNewTab}
            custom_click_track_value={joinAndConnectButtonClickTrack}
            data-custom_click_track_value={joinAndConnectButtonClickTrack}
            data-is-click-track={true}
            ariaLabel={pageProps?.joinAndConnectButton}
            isDisabled={pageProps?.isAuthorMode ? false : !pageProps?.enableLoyaltyJoinNowBtn}
          >
            {!pageProps?.enableLoyaltyJoinNowBtn ? (
              <div className="m-spinner-s" data-testid="activate-spinner"></div>
            ) : (
              <span>{pageProps?.joinAndConnectButton}</span>
            )}
          </Button>
        </div>
      </div>
      {userExperience?.toLowerCase() === NEW_AGE_UX.marketingWithIncentive ? (
        <div className="w-100">
          {/* Separator */}
          <div className={`t-accent-color mt-4 internetbar-marketing-content__separator`}></div>

          {pageProps?.marketingOffersTnC ? (
            <p
              ref={termsAndConditionsRef}
              className={`t-font-xs internetbar-marketing-content__footer-container__tnc-text`}
              dangerouslySetInnerHTML={{
                __html: marketingWithIncentiveOffersTermsAndConditionsText,
              }}
            ></p>
          ) : null}

          {showMarketingOffersTermsAndConditionsText ? (
            <p
              className={`t-font-xs internetbar-marketing-content__footer-container__tnc-text internetbar-marketing-content__footer-container__offers-tnc-text`}
              dangerouslySetInnerHTML={{
                __html: offersTermsConditionText,
              }}
            ></p>
          ) : null}
        </div>
      ) : null}
    </div>
  ) : null;
};
